<script>
import { ref, computed, watch, onBeforeMount, onBeforeUnmount } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import { isUndefined, isEmpty, without } from 'lodash'

import store from '@/store'
import { useCollections, useAssetPropertyOptions } from '@/features'
import {
  MeasureContent,
  Filters,
  ContentControls,
  Sidebar,
  FilterPills,
  MetricsBar
} from '@/components/Measure'
import CollectionModal from '@/components/Assets/CollectionModal.vue'
import AddToCollectionModal from '@/components/Assets/AddToCollectionModal.vue'
import RemoveFromCollectionModal from '@/components/Assets/RemoveFromCollectionModal.vue'
import SavedSearchForm from '@/components/SavedSearchForm'
import TableColumnsModal from '@/components/Measure/TableColumnsModal'
import ExportModal from '@/components/Measure/ExportModal'
import router from '@/router'

export default {
  name: 'Measure',

  components: {
    MeasureContent,
    Filters,
    ContentControls,
    CollectionModal,
    AddToCollectionModal,
    RemoveFromCollectionModal,
    Sidebar,
    SavedSearchForm,
    FilterPills,
    MetricsBar,
    TableColumnsModal,
    ExportModal
  },

  setup () {
    // Features
    const toast = useToast()
    const collections = useCollections()
    const brandId = store.state.brand.settings.id
    const assetPropertyOptions = useAssetPropertyOptions()

    // Assets
    const assetsLoading = ref(true)
    const loadingNewPage = ref(false)
    const assets = computed(() => store.state.measure.assets)
    const totalAssetCount = computed(() => store.state.measure.totalAssets)
    const hasMoreAssets = computed(() => assets.value.length < totalAssetCount.value)
    const searchQueryParams = computed(() => store.getters['measure/searchQueryParams'])
    const getAssets = async () => {
      await store.dispatch('measure/getAssets', searchQueryParams.value)
    }
    const getMoreAssets = async () => {
      await store.dispatch('measure/getMoreAssets', searchQueryParams.value)
    }
    const getTotals = async () => {
      await store.dispatch('measure/getSearchTotals', searchQueryParams.value)
    }
    const onContentNextPage = async () => {
      assetsLoading.value = true
      loadingNewPage.value = true
      store.commit('measure/setPage', page.value + 1)
      await updateClientUrl()
      await getMoreAssets()
      assetsLoading.value = false
      loadingNewPage.value = false
    }
    const onAssetTablePageChange = async newPage => {
      assetsLoading.value = true
      loadingNewPage.value = true
      store.commit('measure/setPage', newPage)
      await updateClientUrl()
      await getAssets()
      assetsLoading.value = false
      loadingNewPage.value = false
    }

    // Filters
    const page = computed(() => store.state.measure.page)
    const filters = computed(() => store.state.measure.filters)
    const sort = computed(() => store.state.measure.sort)
    const resetFilters = async () => {
      assetsLoading.value = true

      await store.dispatch('measure/resetFilters', brandId)
      await updateClientUrl()
      await Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    }
    const onFilterPillClear = async ({ key, value }) => {
      if (key === 'savedSearch') {
        const result = await resetFilters()
        return result
      }

      let filterValue

      if (Array.isArray(filters.value[key])) {
        filterValue = without(filters.value[key], value)
      } else {
        filterValue = store.getters['measure/defaultFilter'](key)
      }

      assetsLoading.value = true
      store.commit('measure/setFilter', { key, value: filterValue })
      store.commit('measure/setPage', 1)
      await updateClientUrl()
      await Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    }
    const onFilterPillClearAll = async () => {
      const payload = {
        savedSearch: store.getters['measure/defaultFilter']('savedSearch'),
        keywords: store.getters['measure/defaultFilter']('keywords'),
        topics: store.getters['measure/defaultFilter']('topics'),
        collection: store.getters['measure/defaultFilter']('collection'),
        customProperties: store.getters['measure/defaultFilter']('customProperties')
      }

      await onFiltersBulkChange(payload)
    }
    const onFiltersChange = async ({ key, value }) => {
      if (key !== 'quickInsight') {
        store.commit('measure/setFilter', { key: 'quickInsight', value: null })
      }
      assetsLoading.value = true
      store.commit('measure/setFilter', { key, value })
      store.commit('measure/setPage', 1)
      await updateClientUrl()
      await Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    }
    const onQuickInsightSelect = async pk => {
      await store.dispatch('measure/resetFilters', brandId)
      await onFiltersChange({ key: 'quickInsight', value: pk })
    }
    const onFiltersBulkChange = async payload => {
      assetsLoading.value = true
      store.commit('measure/setFiltersFromObject', Object.assign({}, payload, { quickInsight: null }))
      store.commit('measure/setPage', 1)
      await updateClientUrl()
      await Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    }
    const onSortChange = async ({ key, value }) => {
      assetsLoading.value = true
      store.commit('measure/setSort', { key, value })
      store.commit('measure/setPage', 1)
      await updateClientUrl()
      await getAssets()
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    }

    // Filter Options
    const domainOptions = computed(() => store.state.measure.dynamicOptions.domains)
    const topicOptions = computed(() => store.state.measure.dynamicOptions.topics)
    const languageOptions = computed(() => store.state.measure.dynamicOptions.languages)
    const savedSearchOptions = computed(() => store.state.measure.dynamicOptions.savedSearches)
    const quickInsightOptions = computed(() => store.state.measure.dynamicOptions.quickInsights)
    const savedSearchesLoading = computed(() => store.getters['measure/optionLoading']('savedSearches'))
    const propertyOptions = computed(() =>
      assetPropertyOptions.options.value
        .map(opt => ({
          label: opt.value,
          value: `${opt.property__name}__${opt.value}`,
          section: opt.property__name
        }))
    )
    const loadOptions = async () => {
      await Promise.all([
        store.dispatch('measure/loadDomainOptions', brandId),
        store.dispatch('measure/loadLanguageOptions', brandId),
        store.dispatch('measure/loadKeywordOptions', { brand_ids: [ brandId ] }),
        assetPropertyOptions.loadOptions({ brandId }),
        collections.getList(brandId),
        store.dispatch('measure/loadSavedSearchOptions', brandId),
        store.dispatch('measure/loadTopicOptions', { brand_id: brandId }),
        store.dispatch('measure/loadQuickInsightOptions')
      ])
    }

    // UI
    const view = ref('grid')
    const showAddToCollectionModal = ref(false)
    const showRemoveFromCollectionModal = ref(false)
    const showCreateCollectionModal = ref(false)
    const showSavedSearchModal= ref(false)
    const showColumnsModal = ref(false)
    const showExportModal= ref(false)
    const selectedTableColumns = computed(() => store.state.measure.tableColumns)
    const onChangeView = async value => {
      view.value = value
      if (value === 'table') { return }
      const result = await resetFilters()
      return result
    }
    const onTableColumnsModalSubmit = async payload => {
      assetsLoading.value = true
      await store.dispatch('measure/setTableColumns', payload)
      showColumnsModal.value = false
      await  Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
    }
    const updateClientUrl = async () => {
      await store.dispatch('measure/updateBrowserUrl', store.getters['measure/browserQueryParams'])
    }
    watch(view, async () => {
      assetsLoading.value = true
      store.commit('measure/setPage', 1)
      await updateClientUrl()
      await Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
      selectedAssetIndexes.value = []
    })

    // Asset Select
    const selectedAssetIndexes = ref([])
    const selectedAssetIds = computed(() =>
      selectedAssetIndexes.value
        .filter(idx => !isUndefined(assets.value[idx]))
        .map(idx => assets.value[idx].page_url_id)
    )

    // Saved Search
    const updateSavedSearch = ref(false)
    const selectedSavedSearch = computed(() => {
      if (isEmpty(filters.value.savedSearch)) {
        return null
      } else {
        return savedSearchOptions.value
          .find(s => s.value === filters.value.savedSearch)
      }
    })
    const onSaveSearch = async ({ name }) => {
      showSavedSearchModal.value = false
      try {
        await store.dispatch('measure/createSavedSearch', { name, brandId })
        toast.success('Saved search created')
      } catch (error) {
        console.error(error)
        toast.error('Error creating saved search')
      } finally {
        await store.dispatch('measure/loadSavedSearchOptions', brandId)
      }
    }
    const onUpdateSavedSearch = async ({ name }) => {
      showSavedSearchModal.value = false
      try {
        await store.dispatch('measure/updateSavedSearch', {
          pk: filters.value.savedSearch,
          name,
          brandId
        })
        toast.success('Saved search updated')
      } catch (error) {
        console.error(error)
        toast.error('Error updating saved search')
      } finally {
        await store.dispatch('measure/loadSavedSearchOptions', brandId)
        updateSavedSearch.value = false
      }
    }
    const onUpdateSearchAction = () => {
      updateSavedSearch.value = true
      showSavedSearchModal.value = true
    }
    const onCancelUpdateSavedSearch = () => {
      updateSavedSearch.value = false
      showSavedSearchModal.value = false
    }
    const onSavedSearchSelect = async searchId => {
      assetsLoading.value = true
      store.commit('measure/setPage', 1)

      if (String(searchId) === filters.value.savedSearch || isEmpty(searchId)) {
        await resetFilters()
      } else {
        const savedSearch = savedSearchOptions.value.find(s => s.value === String(searchId))

        store.commit('measure/setFiltersFromSavedSearch', savedSearch.q)
        store.commit('measure/setSortFromSavedSearch', savedSearch.q)
        store.commit('measure/setFilter', { key: 'savedSearch', value: String(searchId) })

        await updateClientUrl()
        await Promise.all([getAssets(), getTotals()])
        assetsLoading.value = false
        selectedAssetIndexes.value = []
      }
    }

    // Metrics
    const metrics = computed(() => store.state.measure.metrics)
    const queryMetrics = computed(() => store.state.measure.metrics)

    // Collections
    const collectionOptions = computed(() =>
      collections.list.value
        .map(c => ({
          label: c.name,
          value: String(c.id),
          assets: c.asset_set
        }))
    )
    const collectionOptionsForAddToCollectionModal = computed(() =>
      collectionOptions.value
        .map(obj => ({ name: obj.label, id: obj.value, assets: obj.assets }))
    )
    const selectedCollection = computed(() => {
      if (isEmpty(filters.value.collection)) {
        return null
      } else {
        return collectionOptions.value
          .find(item => item.value === filters.value.collection)
      }
    })
    const onSelectAllAssets = () => {
      selectedAssetIndexes.value = assets.value.map((_asset, idx) => idx)
    }
    const onUnselectAllAssets = () => { selectedAssetIndexes.value = [] }
    const onCollectionModalCreate = async formData => {
      assetsLoading.value = true
      showCreateCollectionModal.value = false
      try {
        await collections.create(
          formData.name,
          formData.description,
          brandId,
          selectedAssetIds.value
        )
        toast.success('Collection created')
      } catch (error) {
        console.error(error)
        toast.error('Error creating collection')
      } finally {
        selectedAssetIndexes.value = []
        await collections.getList(brandId)
        assetsLoading.value = false
      }
    }
    const onAddToCollection = async collection => {
      assetsLoading.value = true
      showAddToCollectionModal.value = false
      try {
        await collections.addToCollection(
          collection.id,
          collection.assets,
          selectedAssetIds.value
        )
        toast.success('Collection updated')
      } catch (error) {
        console.error(error)
        toast.error('Error updating collection')
      } finally {
        selectedAssetIndexes.value = []
        await collections.getList(brandId)
        assetsLoading.value = false
      }
    }
    const onRemoveFromCollection = async () => {
      assetsLoading.value = true
      showRemoveFromCollectionModal.value = false
      try {
        await collections.removeFromCollection(
          selectedCollection.value.value,
          selectedCollection.value.assets,
          selectedAssetIds.value
        )
        toast.success('Collection updated')
      } catch (error) {
        console.error(error)
        toast.error('Error updating collection')
      } finally {
        store.commit('measure/setPage', 1)
        selectedAssetIndexes.value = []
        await collections.getList(brandId)
        await Promise.all([getAssets(), getTotals()])
        assetsLoading.value = false
      }
    }

    // Misc
    const initState = async () => {
      if (!isEmpty(router.currentRoute.query)) {
        store.commit('measure/setFiltersFromObject', router.currentRoute.query)
        store.commit('measure/setSortFromObject', router.currentRoute.query.sort || {})
      } else {
        if (!store.state.measure.filters.savedSearch) {
          await store.dispatch('measure/resetFilters', brandId)
        } else {
          updateClientUrl()
        }
      }

      if (!router.currentRoute.query.brandIds) {
        store.commit('measure/setFilter', { key: 'brandIds', value: [ String(brandId) ] })
      }
    }

    // Lifecycle Hooks
    onBeforeMount(async () => {
      await initState()
      await Promise.all([getAssets(), getTotals()])
      assetsLoading.value = false
      await loadOptions()
    })
    onBeforeUnmount(() => { store.commit('measure/resetStateToDefault') })

    return {
      filters,
      domainOptions,
      languageOptions,
      propertyOptions,
      topicOptions,
      collectionOptions,
      totalAssetCount,
      metrics,
      assetsLoading,
      collectionsLoading: collections.loading,
      savedSearchOptions,
      savedSearchesLoading,
      quickInsightOptions,
      sort,
      selectedAssetIndexes,
      view,
      showCreateCollectionModal,
      showAddToCollectionModal,
      showRemoveFromCollectionModal,
      showSavedSearchModal,
      showColumnsModal,
      showExportModal,
      assets,
      hasMoreAssets,
      loadingNewPage,
      page,
      selectedTableColumns,
      queryMetrics,
      collectionOptionsForAddToCollectionModal,
      selectedCollection,
      searchQueryParams,
      updateSavedSearch,
      selectedSavedSearch,

      onSortChange,
      onFiltersChange,
      onFiltersBulkChange,
      onSavedSearchSelect,
      onQuickInsightSelect,
      onSelectAllAssets,
      onUnselectAllAssets,
      onChangeView,
      onUpdateSearchAction,
      onFilterPillClear,
      onFilterPillClearAll,
      onContentNextPage,
      onAssetTablePageChange,
      onCollectionModalCreate,
      onAddToCollection,
      onRemoveFromCollection,
      onTableColumnsModalSubmit,
      onCancelUpdateSavedSearch,
      onSaveSearch,
      onUpdateSavedSearch
    }
  }
}
</script>

<template>
  <div id="measureView">
    <div class="measureView-container">
      <section class="measureView-filters">
        <filters :filters="filters"
          :domainOptions="domainOptions"
          :languageOptions="languageOptions"
          :propertyOptions="propertyOptions"
          :topicOptions="topicOptions"
          :collectionOptions="collectionOptions"
          @change="onFiltersChange"
          @bulkChange="onFiltersBulkChange"
        ></filters>
      </section>

      <section class="measureView-metrics">
        <metrics-bar :totalCount="totalAssetCount"
          :metrics="metrics"
          :loading="assetsLoading"
        ></metrics-bar>
      </section>

      <section class="measureView-sidebar">
        <sidebar :collectionOptions="collectionOptions"
          :selectedCollection="filters.collection"
          :collectionsLoading="collectionsLoading"
          :savedSearchOptions="savedSearchOptions"
          :savedSearchLoading="savedSearchesLoading"
          :selectedSavedSearch="filters.savedSearch"
          :quickInsightOptions="quickInsightOptions"
          :selectedQuickInsight="filters.quickInsight"
          @change="onFiltersChange"
          @saved-search-select="onSavedSearchSelect"
          @quick-insight-select="onQuickInsightSelect"
        ></sidebar>
      </section>

      <section class="measureView-controls">
        <content-controls :sort="sort"
          :selectedAssetIndexes="selectedAssetIndexes"
          :selectedCollectionId="filters.collection"
          :view="view"
          :selectedSavedSearch="filters.savedSearch"
          @changeSort="onSortChange"
          @create-collection="showCreateCollectionModal = true"
          @add-to-collection="showAddToCollectionModal = true"
          @remove-from-collection="showRemoveFromCollectionModal = true"
          @select-all="onSelectAllAssets"
          @unselect-all="onUnselectAllAssets"
          @change-view="onChangeView"
          @save-search="showSavedSearchModal = true"
          @update-search="onUpdateSearchAction"
          @customizeColumns="showColumnsModal = true"
          @export="showExportModal = true"
        ></content-controls>
      </section>

      <section class="measureView-filterPills">
        <filter-pills :filters="filters"
          :collectionOptions="collectionOptions"
          :savedSearchOptions="savedSearchOptions"
          :quickInsightOptions="quickInsightOptions"
          @clear="onFilterPillClear"
          @clear-all="onFilterPillClearAll"
        ></filter-pills>
      </section>

      <section class="measureView-assets">
        <measure-content :assets="assets"
          :hasMoreAssets="hasMoreAssets"
          :totalAssetCount="totalAssetCount"
          :view="view"
          :assetsLoading="assetsLoading"
          :loadingNewPage="loadingNewPage"
          :page="page"
          :selectedAssetIndexes="selectedAssetIndexes"
          :columns="selectedTableColumns"
          :queryMetrics="queryMetrics"
          :metricDateStart="filters.metricDateStart"
          :metricDateEnd="filters.metricDateEnd"
          @selectedAssetIndexesChanged="value => selectedAssetIndexes = value"
          @next-page="onContentNextPage"
          @page-change="onAssetTablePageChange"
        ></measure-content>
      </section>
    </div>

    <collection-modal :showModal.sync="showCreateCollectionModal"
      :selectedAssetIndexes="selectedAssetIndexes"
      @create="onCollectionModalCreate"
    ></collection-modal>

    <add-to-collection-modal :showModal.sync="showAddToCollectionModal"
      :collectionOptions="collectionOptionsForAddToCollectionModal"
      :selectedAssetIndexes="selectedAssetIndexes"
      @submit="onAddToCollection"
    ></add-to-collection-modal>

    <remove-from-collection-modal :showModal.sync="showRemoveFromCollectionModal"
      :collectionName="selectedCollection ? selectedCollection.name || selectedCollection.label : ''"
      :selectedAssetCount="selectedAssetIndexes.length"
      @submit="onRemoveFromCollection"
    ></remove-from-collection-modal>

    <table-columns-modal :isOpen="showColumnsModal"
      :selectedColumns="selectedTableColumns"
      @close="showColumnsModal = false"
      @submit="onTableColumnsModalSubmit"
    ></table-columns-modal>

    <export-modal :show-modal.sync="showExportModal"
      :queryParams="searchQueryParams"
    ></export-modal>

    <neb-modal :isOpen="showSavedSearchModal"
      title="Save Search"
      @clickOutside="onCancelUpdateSavedSearch"
    >
      <saved-search-form v-if="!updateSavedSearch"
        @cancel="showSavedSearchModal = false"
        @submit="onSaveSearch"
      ></saved-search-form>

      <saved-search-form v-if="updateSavedSearch"
        :name="selectedSavedSearch.q.name || ''"
        @cancel="onCancelUpdateSavedSearch"
        @submit="onUpdateSavedSearch"
      ></saved-search-form>
    </neb-modal>
  </div>
</template>

<style lang="scss" scoped>
#measureView {
  padding-bottom: 40px;
}

.measureView {
  &-container {
    display: grid;
    grid-template-columns: auto 210px 975px auto;
    grid-template-rows: auto auto auto auto 1fr;
    min-height: 80vh;
  }

  &-filters {
    grid-column: 2 / span 2;
    grid-row: 1 / span 1;
  }

  &-metrics {
    grid-column: 2 /span 2;
    grid-row: 2 /span 1;
    padding-bottom: 25px;
  }

  &-sidebar {
    grid-column: 2 / span 1;
    grid-row: 3 / -1;
  }

  &-controls {
    grid-column: 3 / span 1;
    grid-row: 3 / span 1;
  }

  &-filterPills {
    grid-column: 3 / span 1;
    grid-row: 4 / span 1;
  }

  &-assets {
    grid-column: 3 / span 1;
    grid-row: 5 / -1;
  }
}
</style>
